/*
=========================================================
* RingFoods Visibility Section - "Total Visibility, Zero Guesswork"
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

// @mui icons
import BarChartIcon from "@mui/icons-material/BarChart";
import ArticleIcon from "@mui/icons-material/Article";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import SmsIcon from "@mui/icons-material/Sms";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ForumIcon from "@mui/icons-material/Forum";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import { brandPrimary, grey100 } from "pages/RingFoodsHomepage/theme";

const features = [
  {
    icon: BarChartIcon,
    title: "Real-time Analytics",
    description:
      "Call volume trends, type breakdowns, peak hour patterns, and satisfaction scores. Compare with last period. Export to CSV anytime.",
  },
  {
    icon: ArticleIcon,
    title: "Call Transcripts & Recordings",
    description:
      "Read every AI conversation word-for-word and listen to the actual call audio. Filter by call type, search by phone number, download recordings, export for your records.",
  },
  {
    icon: ForumIcon,
    title: "Customer Feedback Reports",
    description:
      "Every compliment and complaint is captured, classified by sentiment and severity, and tracked from new to resolved. Critical complaints alert you instantly.",
  },
  {
    icon: NotificationsActiveIcon,
    title: "Smart Notifications",
    description:
      "Choose exactly what you're notified about \u2014 orders, reservations, complaints, inquiries, dropped calls \u2014 via SMS, email, or both.",
  },
  {
    icon: SmsIcon,
    title: "Customer SMS Confirmations",
    description:
      "Every reservation and order gets an instant text confirmation to the caller. Professional, automatic, no extra work from your team.",
  },
  {
    icon: CalendarMonthIcon,
    title: "Reservation Calendar",
    description:
      "Visual drag-and-drop calendar for managing reservations. Create, move, resize \u2014 just like Google Calendar but built for your tables. Cancel a booking and the AI automatically calls the customer to let them know.",
  },
  {
    icon: ReceiptLongIcon,
    title: "Order Management",
    description:
      "Track every order from pending to completed. See daily revenue, filter by status, auto-refresh in real time. Includes takeout and delivery orders.",
  },
  {
    icon: HourglassTopIcon,
    title: "Waitlist & Queue Management",
    description:
      "When you're fully booked, the AI offers the waitlist — by phone or from your free webpage. Guests see their live position and get texted the moment a table opens. On Professional and Enterprise plans, the AI can even call them with the open spot.",
  },
];

function Visibility() {
  return (
    <MKBox component="section" py={{ xs: 8, md: 12 }} sx={{ backgroundColor: grey100 }}>
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Total Visibility, Zero Guesswork
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" } }}>
              See every call, every order, every reservation &mdash; all in one dashboard built for restaurant owners.
            </MKTypography>
          </Grid>
        </Grid>

        {/* 2-column horizontal card grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} md={6} key={index}>
                <MKBox
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid #E5E5E5",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    },
                  }}
                >
                  {/* Icon Circle */}
                  <MKBox
                    sx={{
                      width: 56,
                      height: 56,
                      minWidth: 56,
                      borderRadius: "50%",
                      backgroundColor: "rgba(26, 26, 26, 0.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: brandPrimary }} />
                  </MKBox>

                  {/* Text */}
                  <MKBox>
                    <MKTypography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                      {feature.title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                      {feature.description}
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Visibility;
